import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, GlobeIcon, UserIcon, SmartphoneIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { dxFinalCta as c, dxFinalEntries, type Bi, type FinalEntryKey } from '../../data/digitalExperienceFinalCta';
const EASE = [0.23, 1, 0.32, 1] as const;
const ICONS: Record<FinalEntryKey, typeof GlobeIcon> = {
  web: GlobeIcon,
  portal: UserIcon,
  app: SmartphoneIcon
};
function EntryIcon({
  k
}: {
  k: FinalEntryKey;
}) {
  const Icon = ICONS[k];
  return <span className="relative z-10 w-[104px] h-[104px] lg:w-[118px] lg:h-[118px] rounded-full bg-white shadow-[0_20px_44px_-22px_rgba(79,70,229,0.45)] flex items-center justify-center" data-id="element-3239" data-mp="28skob3zu8v21">
      <Icon className="w-11 h-11 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-3240" data-mp="eea3r7qa0n22" />
    </span>;
}
export function DigitalFinalCTA() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="relative max-w-[1500px] mx-auto" data-id="element-3241" data-mp="humx8xlqu4up">
      <div className="grid grid-cols-1 lg:grid-cols-[52fr_48fr] gap-14 lg:gap-8 items-center" data-id="element-3242" data-mp="d1jymb24057n">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="min-w-0" data-id="element-3243" data-mp="81xowblhp73l">
          <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-3244" data-mp="10pblu9p6u2np">{pick(c.eyebrow)}</p>
          <h2 className="font-serif text-[2.6rem] sm:text-[3.6rem] 2xl:text-[4.6rem] leading-[1.04] tracking-tight mt-6" data-id="element-3245" data-mp="qsp2xqgf9lob">
            <span className="block text-[#121A3A]" data-id="element-3246" data-mp="ltc74g7kp49l">{pick(c.navy)}</span>
            {c.indigo.map(l => <span key={l.fr} className="block text-[#4F46E5]" data-id="element-3247" data-mp="3bkiiydnn3sdl">{pick(l)}</span>)}
          </h2>
          <p className="font-sans text-[17px] md:text-[19px] text-[#4A5268] leading-relaxed mt-7 max-w-[640px]" data-id="element-3248" data-mp="36k35lm3cykvm">{pick(c.body)}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4" data-id="element-3249" data-mp="1ylp3008hqted">
            <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
            location: 'Digital Experience Final'
          })} className="inline-flex items-center justify-center gap-4 rounded-xl bg-[#4F46E5] px-10 py-4 font-sans text-[17px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] whitespace-nowrap" data-id="element-3250" data-mp="22msjmoyd4tgn">
              {pick(c.primary)}
              <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-3251" data-mp="15w8gfj7jy6q2" />
            </Link>
            <Link to="/request-demo" onClick={() => trackEvent('Contact CTA Clicked', {
            location: 'Digital Experience Final'
          })} className="inline-flex items-center justify-center rounded-xl border border-[#DCDAF5] bg-white px-10 py-4 font-sans text-[17px] font-medium text-[#121A3A] transition-colors duration-150 hover:border-[#C9C5F5] whitespace-nowrap" data-id="element-3252" data-mp="1av36953xsvjo">
              {pick(c.secondary)}
            </Link>
          </div>
        </motion.div>

        {/* Three connected entry points — horizontal from sm up */}
        <div className="relative hidden sm:block" data-id="element-3253" data-mp="r7ghy7u66q2t">
          <svg className="absolute left-[10%] right-[10%] top-[34px] w-[80%] h-[60px]" viewBox="0 0 600 60" preserveAspectRatio="none" aria-hidden="true" data-id="element-3254" data-mp="ma6s4r2vcceg">
            <path d="M0 50 C 150 12, 450 12, 600 50" fill="none" stroke="#A7A2F4" strokeWidth={1.4} vectorEffect="non-scaling-stroke" data-id="element-3255" data-mp="107v57cqp2f8y" />
            {[150, 450].map(x => <g key={x} data-id="element-3256" data-mp="19z99ryxi658y">
                <circle cx={x} cy={24} r={7} fill="#4F46E5" opacity={0.14} data-id="element-3257" data-mp="1a1vjo57v8wuc" />
                <circle cx={x} cy={24} r={3} fill="#6D66F0" data-id="element-3258" data-mp="151pucvp2jmff" />
              </g>)}
          </svg>
          <ul className="relative grid grid-cols-3" data-id="element-3259" data-mp="1jw48ahts05n8">
            {dxFinalEntries.map(e => <li key={e.key} className={`flex flex-col items-center text-center ${e.key === 'portal' ? '-mt-6' : ''}`} data-id="element-3260" data-mp="1ex11osy5drd5">
                <EntryIcon k={e.key} data-id="element-3261" data-mp="3dgxq0ic0xm5d" />
                <p className="mt-4 font-sans text-[17px] lg:text-[19px] leading-snug text-[#121A3A]" data-id="element-3262" data-mp="3dfw4tl1anj9x">
                  {e.label.map(l => <span key={l.fr} className="block" data-id="element-3263" data-mp="3i8fs59ps9srx">{pick(l)}</span>)}
                </p>
              </li>)}
          </ul>
        </div>

        {/* Mobile — vertical sequence */}
        <ul className="sm:hidden flex flex-col items-center" data-id="element-3264" data-mp="2pkjn9hq28uk2">
          {dxFinalEntries.map((e, i) => <li key={e.key} className="flex flex-col items-center text-center" data-id="element-3265" data-mp="2uibpp5obzz2i" data-mpi={i}>
              {i > 0 && <span className="w-px h-8 bg-[#C9C5F5] mb-3" aria-hidden="true" data-id="element-3266" data-mp="2zflc0cgfbum4" />}
              <EntryIcon k={e.key} data-id="element-3267" data-mp="34halhno00083" />
              <p className="mt-3 font-sans text-[17px] text-[#121A3A]" data-id="element-3268" data-mp="34e6def7ewkc5">{e.label.map(l => pick(l)).join(' ')}</p>
            </li>)}
        </ul>
      </div>

      <div className="relative mt-16 lg:mt-12 flex items-center" aria-hidden="true" data-id="element-3269" data-mp="jopc7wano7vp">
        <span className="flex-1 h-px bg-[#E6E5F2]" data-id="element-3270" data-mp="248celr1zqjm6" />
        <span className="w-2 h-2 rounded-full bg-[#A7A2F4] mx-3" data-id="element-3271" data-mp="249vtbd800mlu" />
        <span className="flex-1 h-px bg-[#E6E5F2]" data-id="element-3272" data-mp="1z9hv0mw78r10" />
      </div>

      <p className="mt-10 text-center font-serif text-[2rem] sm:text-[2.8rem] lg:text-[3.3rem] leading-[1.1] tracking-tight" data-id="element-3273" data-mp="1uhpzrz23lx4j">
        <span className="block text-[#121A3A]" data-id="element-3274" data-mp="1pidhzsq33g8t">{pick(c.signatureNavy)}</span>
        <span className="block text-[#4F46E5]" data-id="element-3275" data-mp="1kkbxd3d0f8v8">{pick(c.signatureIndigo)}</span>
      </p>
    </div>;
}